/**
 * @fileoverview Shared Locus runtime flag constants + payload parsing
 * (FRE-1231 / FRE-1270).
 *
 * Kept free of `'use client'` so both the client gate
 * (`locusRuntime.ts`) and the server evaluator (`server.ts`) can import
 * the same fail-closed payload contract without dragging React / PostHog
 * client code into the server bundle.
 */

/** PostHog feature-flag key for the Locus runtime launch gate. */
export const LOCUS_RUNTIME_FLAG = 'locus-runtime';

/**
 * Route groups the staged rollout can enable, i.e. every Locus-mountable row
 * of the PRD §6.3 matrix (`(admin)` is excluded by the mount gate itself and
 * can never be flagged on). Payload `surfaces` entries must match exactly.
 */
export const LOCUS_ROLLOUT_ROUTE_GROUPS = ['app', 'dynamic', 'city', 'apex'] as const;

/** A route group eligible for the staged rollout. */
export type LocusRolloutRouteGroup = (typeof LOCUS_ROLLOUT_ROUTE_GROUPS)[number];

/**
 * Server / BFF evaluation result for `locus-runtime` (FRE-1270). `surfaces` is
 * already fail-closed-validated — never trust remote JSON without running it
 * through {@link parseLocusRolloutSurfaces} first.
 */
export interface LocusRuntimeEvaluation {
  readonly enabled: boolean;
  readonly surfaces: readonly LocusRolloutRouteGroup[];
}

/** Fail-closed default — flag off, no surfaces. */
export const LOCUS_RUNTIME_DISABLED: LocusRuntimeEvaluation = {
  enabled: false,
  surfaces: [],
};

export function isRolloutRouteGroup(value: string): value is LocusRolloutRouteGroup {
  return (LOCUS_ROLLOUT_ROUTE_GROUPS as readonly string[]).includes(value);
}

/**
 * Strictly parse the `locus-runtime` flag payload into the set of enabled
 * route groups. The payload is untrusted remote JSON, so anything that is not
 * positively `{ surfaces: string[] }` yields the **empty set** (off
 * everywhere): non-object payloads, missing / non-array `surfaces`, etc.
 *
 * Entries inside a well-formed `surfaces` array are validated individually —
 * non-strings and unknown names are dropped, never granted, so a typo in a
 * new stage can't turn anything on (and can't turn already-live stages off).
 */
export function parseLocusRolloutSurfaces(payload: unknown): ReadonlySet<LocusRolloutRouteGroup> {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return new Set();
  }
  const surfaces = (payload as Record<string, unknown>).surfaces;
  if (!Array.isArray(surfaces)) {
    return new Set();
  }
  const enabled = new Set<LocusRolloutRouteGroup>();
  for (const entry of surfaces) {
    if (typeof entry === 'string' && isRolloutRouteGroup(entry)) {
      enabled.add(entry);
    }
  }
  return enabled;
}

/**
 * Whether a server-evaluated flag state enables the given route group.
 * Requires `enabled === true` AND a surfaces list containing the group.
 */
export function isLocusRuntimeEvaluationEnabledFor(
  evaluation: LocusRuntimeEvaluation,
  routeGroup: string,
): boolean {
  if (!evaluation.enabled) return false;
  if (!isRolloutRouteGroup(routeGroup)) return false;
  return evaluation.surfaces.includes(routeGroup);
}
